import { useStyletron } from '@thanx/uikit/theme'
import React from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

export interface PaginationInfoProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  /** Content to display (text nodes, spans, etc.) */
  children: React.ReactNode
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Controlled PaginationInfo component.
 *
 * A styled container for pagination text. Accepts children to allow
 * composition with any text content (spans, numbers, styled text, etc.).
 *
 * @example
 * ```tsx
 * <PaginationInfo>
 *   <span>{startItem}</span> - <span>{endItem}</span> of <span>{totalCount}</span>
 * </PaginationInfo>
 * ```
 */
const PaginationInfo = React.memo(
  ({ children, className, style, ...props }: PaginationInfoProps) => {
    const [css, theme] = useStyletron()

    const infoClassName = css(getInfoStyles(theme))

    return (
      <span
        className={`${infoClassName} ${className || ''}`}
        style={style}
        {...props}
      >
        {children}
      </span>
    )
  }
)

PaginationInfo.displayName = 'PaginationInfo'

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

const getInfoStyles = (theme: any) => ({
  fontSize: '13px',
  color: theme.colors.grey50,
  whiteSpace: 'nowrap' as const,
})

export default PaginationInfo
